import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../Utils/Axios';
import DashboardHeader from '../CompanyComponents/DashboardHeaderRouter';
import RecoveryDetailsP2 from './RecoveryDetailsP2';
import LoadingScreen from '../../Components/LoadingScreen';
import { useBackspaceNavigation } from '../../hooks/useBackspaceNavigation';

function RecoveryDetailsP2Wrapper() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const dateParam = searchParams.get('date');

    // Backspace navigation to dashboard
    useBackspaceNavigation();

    const [company, setCompany] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [companyLanguage, setCompanyLanguage] = useState('ur');
    const [ledgerParties, setLedgerParties] = useState([]);
    const [sales, setSales] = useState([]);
    const [recoveries, setRecoveries] = useState([]);

    useEffect(() => {
        fetchCompanyProfile();
    }, []);

    const getToken = () => {
        return localStorage.getItem("companyToken");
    };

    const fetchCompanyProfile = async () => {
        try {
            setIsLoading(true);
            const token = getToken();
            if (!token) {
                navigate('/company/login');
                return;
            }

            const response = await api.get("/company/profile", {
                headers: { Authorization: `Bearer ${token}` }
            });

            const companyData = response.data.company;
            setCompany(companyData);
            const lang = companyData.websiteLanguage || companyData.language || 'ur';
            setCompanyLanguage(lang);

            // Fetch sales, recoveries, and ledger parties
            await Promise.allSettled([
                fetchSales(),
                fetchRecoveries(),
                fetchLedgerParties()
            ]);
        } catch (error) {
            console.error('Fetch company error:', error);
            toast.error('Failed to load company data');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchLedgerParties = async () => {
        try {
            const token = getToken();
            const response = await api.get("/ledger/parties?type=customer", {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLedgerParties(response.data.parties || []);
        } catch (error) {
            console.error('Fetch ledger parties error:', error);
        }
    };

    const fetchSales = async () => {
        try {
            const token = getToken();
            const response = await api.get("/sale/company/all", {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSales(response.data.sales || []);
        } catch (error) {
            console.error('Fetch sales error:', error);
        }
    };

    const fetchRecoveries = async () => {
        try {
            const token = getToken();
            const response = await api.get("/recovery/company/all", {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRecoveries(response.data.recoveries || []);
        } catch (error) {
            console.error('Fetch recoveries error:', error);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem("companyToken");
        localStorage.removeItem("companyData");
        navigate("/company/login");
    };

    if (isLoading) {
        return (
            <LoadingScreen
                companyLanguage={companyLanguage}
                message={companyLanguage === "ur" ? "لوڈ ہو رہا ہے..." : "Loading..."}
            />
        );
    }

    return (
        <div className={`min-h-screen bg-slate-50 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
            <DashboardHeader
                company={company}
                companyLanguage={companyLanguage}
                onLogout={handleLogout}
                bills={[]}
                sales={sales}
                recoveries={recoveries}
                persons={[]}
            />
            <div className="max-w-7xl mx-auto px-4 py-6">
                <RecoveryDetailsP2
                    company={company}
                    companyLanguage={companyLanguage}
                    sales={sales}
                    recoveries={recoveries}
                    ledgerParties={ledgerParties}
                    initialDate={dateParam}
                />
            </div>
        </div>
    );
}

export default RecoveryDetailsP2Wrapper;
